import React, { useEffect, useState } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory, useParams } from "react-router";
import { toast } from "sonner";

import ConditionalRenderEditor from "./components/ConditionalRenderEditor";
import ProjectAPI from "../../api/ProjectAPI";
import Input from "../../components/form/Input";
import Select from "../../components/form/Select";
import Breadcrumbs from "../../components/utils/Breadcrumbs";
import ContainerHeader from "../../components/utils/ContainerHeader";

import type { ChangeEvent, JSX } from "react";

import type {
  ConditionRender,
  GetProjectPageComponentListResponse,
  GetProjectPageResponse,
  ShortcutComponent,
  ShortcutComponentMetadataSource,
} from "../../api/ProjectAPI.types";
import type { PlatformRoutesParams } from "../../components/routes/PlatformRoutes";
import type { AppState } from "../../reducers";

function isNewComponent(
  component: ShortcutComponent | undefined,
): component is ShortcutComponent {
  return component?.uuid === "new";
}

function isExistingComponent(
  component: ShortcutComponent | undefined,
): component is ShortcutComponent & { uuid: string } {
  return component?.uuid !== undefined && component.uuid !== "new";
}

function ShortcutComponentPage(): JSX.Element {
  const { projectSlug, platform, componentId, pageId } =
    useParams<PlatformRoutesParams>();
  const isNew = componentId === "new";
  const language = useSelector((state: AppState) => state.variableReducer.lang);

  const getProjectPageComponentListQuery = ProjectAPI.generateQuery({
    fn: ProjectAPI.getProjectPageComponentList,
    params: [{ pageId, platform, projectSlug }],
    options: { enabled: isNew },
  });

  const getComponentQuery = ProjectAPI.generateQuery({
    fn: ProjectAPI.getComponent<ShortcutComponent>,
    params: [{ projectSlug, componentId }],
    options: {
      enabled: !isNew,
    },
  });

  const { data: shortcutComponentData } = useQuery(getComponentQuery);

  const { data: projectPageComponentListData } = useQuery({
    ...getProjectPageComponentListQuery,
    select: (data) => data.components,
  });

  const getProjectPageQuery = ProjectAPI.generateQuery({
    fn: ProjectAPI.getProjectPage,
    params: [{ pageId, projectSlug }],
    options: {
      enabled: isNew,
    },
  });

  const { data: projectPageData } = useQuery(getProjectPageQuery);

  const queryClient = useQueryClient();

  const history = useHistory();

  const { t } = useTranslation("editor-platform");

  const [shortcutComponent, setShortcutComponent] =
    useState<ShortcutComponent>();

  useEffect(() => {
    if (isNew) {
      const newShortcut: ShortcutComponent = {
        storefront_project_page_uuid: pageId,
        uuid: "new",
        dynamic_component: null,
        static_component: null,
        section: "page",
        type: "shortcut",
        platform,
        label: t("shortcutComponentPage.new"),
        enabled: true,
        rank:
          projectPageComponentListData?.length !== undefined
            ? projectPageComponentListData.length + 1
            : 0,
        params: {
          withTitle: true,
          metadataSource: undefined,
          has_conditions: false,
          conditional_render: [],
        },
      };

      setShortcutComponent(newShortcut);
    } else if (shortcutComponentData) {
      setShortcutComponent(shortcutComponentData);
    }
  }, [
    shortcutComponentData,
    platform,
    t,
    language,
    isNew,
    pageId,
    projectPageComponentListData?.length,
  ]);

  const breadcrumbs = [
    {
      name: t("editor-menu:platform"),
      url: "/layout",
    },
    {
      name: t("editor-platform:title.layout"),
      url: "/layout",
    },
    {
      name: t(`editor-platform:page`),
      url: `/layout/pages/${pageId}/${platform}`,
    },
    {
      name: t("shortcut"),
    },
  ];

  const createComponentMutation = useMutation({
    mutationFn: async (component: ShortcutComponent) =>
      ProjectAPI.createComponent({ component, projectSlug }),
    onSuccess: (response) => {
      const oldPath = history.location.pathname;
      const newPath = oldPath.replace(/\/(new)$/, `/${response.uuid}`);
      history.replace(newPath);

      queryClient.setQueryData(
        getProjectPageComponentListQuery.queryKey,
        (
          oldData?: GetProjectPageComponentListResponse,
        ): GetProjectPageComponentListResponse | undefined => {
          if (!oldData) return undefined;
          return {
            ...oldData,
            components: oldData.components.concat(response),
          };
        },
      );

      const hasPlatformInPage =
        projectPageData?.components_platform?.includes(platform);

      if (!hasPlatformInPage) {
        queryClient.setQueryData<GetProjectPageResponse>(
          getProjectPageQuery.queryKey,
          (oldData) => {
            if (!oldData) return oldData;
            return {
              ...oldData,
              components_platform: [
                ...(oldData?.components_platform ?? []),
                platform,
              ],
            };
          },
        );
      }

      toast.success(t("shortcutComponentPage.createSuccess"));
    },
    onError: (error) => {
      toast.error(t("genericError", { ns: "error" }));
      console.error("Error creating new component:", error);
    },
  });

  const updateComponentMutation = useMutation({
    mutationFn: async (component: ShortcutComponent) =>
      ProjectAPI.updateComponent({
        component,
        projectSlug,
      }),
    onSuccess: (response) => {
      queryClient.setQueryData(
        getProjectPageComponentListQuery.queryKey,
        (
          oldData?: GetProjectPageComponentListResponse,
        ): GetProjectPageComponentListResponse | undefined => {
          if (!oldData) return undefined;
          return {
            ...oldData,
            components: oldData.components.map((item) =>
              item.uuid === response.uuid ? response : item,
            ),
          };
        },
      );
      queryClient.setQueryData(getComponentQuery.queryKey, response);

      toast.success(t("shortcutComponentPage.updateSuccess"));
    },
    onError: (error) => {
      toast.error(t("genericError", { ns: "error" }));
      console.error("Error updating component:", error);
    },
  });

  const handleSave = (): void => {
    if (!shortcutComponent) {
      console.error("Shortcut component is not defined");
      return;
    }

    if (shortcutComponent.params?.metadataSource === undefined) {
      toast.error(t("shortcutComponentPage.metadataSourceIsRequired"));
      return;
    }

    const componentToSave = {
      ...shortcutComponent,
      params: {
        ...shortcutComponent.params,
        conditional_render:
          shortcutComponent.params?.conditional_render?.filter(
            (condition) =>
              !!(condition.type && condition.operator && condition.predicat),
          ),
      },
    };

    if (isExistingComponent(componentToSave)) {
      updateComponentMutation.mutate(componentToSave);
    } else if (isNewComponent(componentToSave)) {
      createComponentMutation.mutate(componentToSave);
    }
  };

  const handleChange = <K extends keyof ShortcutComponent>(
    value: ShortcutComponent[K],
    field: K,
  ): void => {
    setShortcutComponent((prevComponent) => {
      if (!prevComponent) return prevComponent;
      return {
        ...prevComponent,
        [field]: value,
      };
    });
  };

  const handleConditionsToggle = (value: boolean): void => {
    setShortcutComponent((prevComponent) => {
      if (!prevComponent) return prevComponent;
      return {
        ...prevComponent,
        params: {
          metadataSource: prevComponent.params?.metadataSource,
          withTitle: prevComponent.params?.withTitle,
          has_conditions: value,
          conditional_render: prevComponent.params?.conditional_render,
        },
      };
    });
  };

  const handleConditionsChange = (value: ConditionRender[]): void => {
    setShortcutComponent((prevComponent) => {
      if (!prevComponent) return prevComponent;
      return {
        ...prevComponent,
        params: {
          metadataSource: prevComponent.params?.metadataSource,
          withTitle: prevComponent.params?.withTitle,
          has_conditions: prevComponent.params?.has_conditions ?? false,
          conditional_render: value,
        },
      };
    });
  };

  interface Option {
    value: ShortcutComponentMetadataSource;
    label: string;
  }

  const methods = useForm<{
    metadataSource?: Option;
    withTitle: boolean;
  }>({
    values: {
      metadataSource: shortcutComponent?.params?.metadataSource
        ? {
            value: shortcutComponent.params.metadataSource,
            label: t(
              `editor-advancedsearch:${shortcutComponent.params.metadataSource}`,
            ),
          }
        : undefined,
      withTitle: shortcutComponent?.params?.withTitle ?? true,
    },
  });

  const selectOptions: Option[] = [
    {
      value: "target_audiences",
      label: t("editor-advancedsearch:target_audiences"),
    },
    {
      value: "new_genres",
      label: t("editor-advancedsearch:new_genres"),
    },
    {
      value: "themes",
      label: t("editor-advancedsearch:themes"),
    },
    {
      value: "formats",
      label: t("editor-advancedsearch:formats"),
    },
  ];

  useEffect(() => {
    const selectedMetadataSource = methods.watch((values, { name }) => {
      if (name === "metadataSource") {
        setShortcutComponent((prevComponent) => {
          if (!prevComponent || !values.metadataSource?.value)
            return prevComponent;
          return {
            ...prevComponent,
            params: {
              ...prevComponent.params,
              metadataSource: values.metadataSource.value,
            },
          };
        });
      }
      if (name === "withTitle") {
        setShortcutComponent((prevComponent) => {
          if (!prevComponent || values.withTitle === undefined)
            return prevComponent;

          return {
            ...prevComponent,
            params: {
              ...prevComponent.params,
              withTitle: values.withTitle,
            },
          };
        });
      }
    });

    return () => {
      selectedMetadataSource.unsubscribe();
    };
  }, [methods]);

  return (
    <div className="size-full overflow-y-auto p-[3.75rem]">
      <Breadcrumbs items={breadcrumbs} />
      {shortcutComponent ? (
        <React.Fragment>
          <ContainerHeader
            withBackBtn
            item={shortcutComponent}
            itemStatus={shortcutComponent.enabled ? "active" : "stop"}
            onSave={handleSave}
            onChangeLabel={(e: ChangeEvent<HTMLInputElement>["target"]): void =>
              handleChange(e.value, "label")
            }
            onChangeState={(isEnabled: boolean): void =>
              handleChange(isEnabled, "enabled")
            }
          />
          <div className="bg-card mt-[1.25rem] rounded-[0.234375rem] p-[1.875rem] shadow">
            <FormProvider
              {...methods} // eslint-disable-line react/jsx-props-no-spreading
            >
              <Input
                isCompact
                isNoWrap
                isReversed
                label={t("showTitle")}
                name="withTitle"
                size="sm"
                type="switch"
              />
              <Select
                isCompact
                isRequired
                label={t("shortcutComponentPage.selectMetadataSource")}
                name="metadataSource"
                options={selectOptions}
              />
            </FormProvider>
            <div className="border-b-border my-[1.5625rem] w-full border-b" />
            <ConditionalRenderEditor
              componentParams={shortcutComponent.params}
              onChangeConditions={handleConditionsChange}
              onChangeConditionsToggle={handleConditionsToggle}
            />
          </div>
        </React.Fragment>
      ) : null}
    </div>
  );
}

export default ShortcutComponentPage;
